import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { RefreshCw, Package, Search, ChevronDown, ChevronUp, Boxes, AlertTriangle, Pencil } from "lucide-react";
import { ProductThumbnail } from "../components/ProductThumbnail";
import { EditInventoryModal } from "../components/EditInventoryModal";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { inferTalla, compareByRow } from "../lib/talla";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryPage = () => {
  const { user } = useAuth();
  const canEdit =
    user?.role === "admin" ||
    user?.role === "gerente_merch" ||
    ((user?.role === "gerente_heladeria" || user?.role === "manager") && !!user?.can_edit_inventory);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editProduct, setEditProduct] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/summary`, getAuthHeaders());
      setSummary(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar inventario");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/inventory/sync`, null, getAuthHeaders());
      const od = r.data.odoo;
      const sh = r.data.shopify;
      const msgs = [];
      if (od.error) msgs.push(`Odoo: ${od.error}`);
      else msgs.push(`Odoo: ${od.updated} SKUs`);
      if (sh.error) msgs.push(`Shopify: ${sh.error}`);
      else msgs.push(`Shopify: ${sh.updated} SKUs`);
      if (od.error || sh.error) toast.error(msgs.join(" · "));
      else toast.success(msgs.join(" · "));
      await fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al sincronizar inventario");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const items = (summary?.items || []).filter(it =>
    !search ||
    (it.internal_product_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (it.variants || []).some(v =>
      (v.label || "").toLowerCase().includes(search.toLowerCase()) ||
      (v.sku || "").toLowerCase().includes(search.toLowerCase())
    )
  );

  const grandTotal = items.reduce((s, i) => s + (i.total_available || 0), 0);
  const totalOdoo = items.reduce((s, i) => s + (i.by_source?.odoo || 0), 0);
  const totalShopify = items.reduce((s, i) => s + (i.by_source?.shopify || 0), 0);

  return (
    <Layout>
    <div className="space-y-6 pb-12" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
            <Boxes className="w-8 h-8 text-[#3B82F6]" />
            Inventario
          </h1>
          <p className="text-[#6B7280] text-sm mt-1">
            Stock disponible por producto, agrupado por canal y ubicación.
            {summary?.last_sync && (
              <span className="ml-2">Última sync: <span className="text-[#4A4A4A] font-medium">{new Date(summary.last_sync).toLocaleString("es-MX")}</span></span>
            )}
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
          data-testid="inventory-sync-button"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Sincronizando..." : "Sincronizar ahora"}
        </Button>
      </div>

      {/* Error banners */}
      {summary?.odoo_error && (
        <div className="p-3 rounded-md bg-[#FEF3C7] border border-[#F59E0B]/30 text-sm text-[#92400E] flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span><strong>Odoo:</strong> {summary.odoo_error}</span>
        </div>
      )}
      {summary?.shopify_error && (
        <div className="p-3 rounded-md bg-[#FEE2E2] border border-[#EF4444]/30 text-sm text-[#991B1B] flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span><strong>Shopify:</strong> {summary.shopify_error}</span>
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#3B82F6]/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-[#6B7280]">Total piezas</p>
              <p className="text-2xl font-bold text-[#3B82F6] font-mono">{grandTotal.toFixed(0)}</p>
            </div>
            <Package className="w-8 h-8 text-[#3B82F6]/40" />
          </CardContent>
        </Card>
        <Card className="bg-white border-[#8B5CF6]/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-[#6B7280]">En Odoo (puntos físicos)</p>
              <p className="text-2xl font-bold text-[#8B5CF6] font-mono">{totalOdoo.toFixed(0)}</p>
              <p className="text-xs text-[#6B7280]">{summary?.odoo_count || 0} SKUs</p>
            </div>
            <Package className="w-8 h-8 text-[#8B5CF6]/40" />
          </CardContent>
        </Card>
        <Card className="bg-white border-[#10B981]/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-[#6B7280]">En Shopify (online)</p>
              <p className="text-2xl font-bold text-[#10B981] font-mono">{totalShopify.toFixed(0)}</p>
              <p className="text-xs text-[#6B7280]">{summary?.shopify_count || 0} SKUs</p>
            </div>
            <Package className="w-8 h-8 text-[#10B981]/40" />
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <Input
          placeholder="Buscar por producto, variante o SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-white"
          data-testid="inventory-search-input"
        />
      </div>

      {/* Items list */}
      {loading ? (
        <Card><CardContent className="p-6 text-center text-[#6B7280]">Cargando inventario...</CardContent></Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-[#6B7280]">
          {summary?.items?.length ? "No hay productos que coincidan con tu búsqueda." : "Aún no hay inventario sincronizado. Toca \"Sincronizar ahora\" para empezar."}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const isOpen = !!expanded[it.internal_product_id];
            return (
              <Card key={it.internal_product_id} className="bg-white border-[#E5E7EB]" data-testid={`inv-card-${it.internal_product_id}`}>
                <CardHeader
                  className="pb-2 cursor-pointer select-none"
                  onClick={() => setExpanded(p => ({ ...p, [it.internal_product_id]: !p[it.internal_product_id] }))}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-sm sm:text-base flex items-center gap-2 flex-wrap min-w-0">
                      {isOpen ? <ChevronUp className="w-4 h-4 text-[#6B7280] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#6B7280] flex-shrink-0" />}
                      <ProductThumbnail image_url={it.image_url} name={it.internal_product_name} size="sm" />
                      <span className="break-words min-w-0">{it.internal_product_name}</span>
                      {it.is_proveedor && <Badge className="bg-[#8B5CF6] text-white text-[10px] py-0">Proveedor</Badge>}
                      {it.is_merch && <Badge variant="outline" className="border-[#F59E0B] text-[#F59E0B] text-[10px] py-0">Merch</Badge>}
                    </CardTitle>
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm flex-wrap">
                      <span className="text-[#8B5CF6]">Odoo: <strong>{(it.by_source?.odoo || 0).toFixed(0)}</strong></span>
                      <span className="text-[#10B981]">Shopify: <strong>{(it.by_source?.shopify || 0).toFixed(0)}</strong></span>
                      <span className="text-[#3B82F6] font-bold text-sm sm:text-base">Total: {it.total_available.toFixed(0)}</span>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#F43F5E] text-[#F43F5E] hover:bg-[#F43F5E]/10 h-7 px-2"
                          onClick={(e) => { e.stopPropagation(); setEditProduct(it); }}
                          title="Ver piezas por talla y ubicación · actualizar o transferir"
                          data-testid={`edit-inventory-${it.internal_product_id}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Actualizar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                {isOpen && (
                  <CardContent>
                    {(it.variants || []).length === 0 ? (
                      <p className="text-sm text-[#6B7280]">Sin variantes registradas.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-[#6B7280] text-xs uppercase tracking-wide">
                              <th className="text-left p-2">Variante</th>
                              <th className="text-left p-2">SKU</th>
                              <th className="text-left p-2">Ubicación</th>
                              <th className="text-right p-2">Disponible</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...it.variants].sort(compareByRow).map((v) => {
                              const rows = v.levels?.length ? v.levels : [{ sku: v.sku, source: null, location_name: "—", available: 0 }];
                              const talla = inferTalla(v.sku, v.label);
                              return rows.map((lvl, i) => (
                                <tr key={`${v.sku}-${i}`} className="border-b last:border-0 hover:bg-[#F9FAFB]">
                                  {i === 0 && (
                                    <td className="p-2 font-medium align-top" rowSpan={rows.length}>
                                      <div className="flex items-center gap-2">
                                        {talla && (
                                          <span className="inline-flex items-center justify-center min-w-[36px] h-7 px-2 rounded-md bg-gradient-to-br from-[#F43F5E] to-[#E11D48] text-white text-xs font-bold shadow-sm">
                                            {talla}
                                          </span>
                                        )}
                                        <span>{v.label}</span>
                                      </div>
                                      <div className="text-xs text-[#6B7280] mt-0.5">Total: <strong>{v.available.toFixed(0)}</strong></div>
                                    </td>
                                  )}
                                  {i === 0 && (
                                    <td className="p-2 font-mono text-xs text-[#6B7280] align-top" rowSpan={rows.length}>{v.sku}</td>
                                  )}
                                  <td className="p-2">
                                    {lvl.source && (
                                      <Badge variant="outline" className={lvl.source === "odoo" ? "border-[#8B5CF6] text-[#8B5CF6] mr-2" : "border-[#10B981] text-[#10B981] mr-2"}>
                                        {lvl.source}
                                      </Badge>
                                    )}
                                    {lvl.location_name}
                                  </td>
                                  <td className={`p-2 text-right font-mono ${lvl.available < 0 ? "text-[#EF4444] font-bold" : "text-[#4A4A4A]"}`}>
                                    {Number(lvl.available).toFixed(0)}
                                  </td>
                                </tr>
                              ));
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
    <EditInventoryModal
      product={editProduct}
      onClose={() => setEditProduct(null)}
      onSaved={() => { setEditProduct(null); fetchSummary(); }}
    />
    </Layout>
  );
};

export default InventoryPage;
